import { FC, Fragment, useState } from "react";
import CustomPopup from "../CustomPopup/CustomPopup";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Divider,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { Colors } from "../../styles/constant";
import CustomThreeDotsLoader from "../CustomThreeDotsLoader/CustomThreeDotsLoader";
import { PolicyOrAccountSelectionData } from "../../globalTypes/GlobalTypes";
import { HlLobCode } from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import { defaultEmptyFun } from "../../utils/helper";
import {
  useAppDispatch,
  useAppSelector,
} from "../../redux-store/CustomReduxHooks";
import { setSelectedTaxCenterFlow } from "../../features/Feature_GeneralAppElements/GeneralAppElements_Slice";
import { useAppTranslation } from "../../hooks/useAppTranslation";

interface Props {
  data: PolicyOrAccountSelectionData;
  policyOrAccountDataHandler: Function;
  fromGuard?: boolean;
  setIsPolicySelectionOpen?: Function;
}

const CustomPolicyOrAccountSelect: FC<Props> = ({
  data,
  policyOrAccountDataHandler,
  fromGuard = false,
  setIsPolicySelectionOpen = defaultEmptyFun,
}) => {
  const [selectedRadioValue, setSelectedRadioValue] = useState<string>("");
  const [isBtnLoading, setIsBtnLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { taxCenterFlow } = useAppSelector(
    (state) => state.GeneralAppElementsSlice,
  );
  const { t } = useAppTranslation("common");

  const handleCloseClick = () => {
    let tempData = { ...data };
    tempData.isOpen = false;
    tempData.lobDetails = null;
    tempData.redirectionUrl = "";
    tempData.options = [];
    tempData.functionalityCode = "";
    if (taxCenterFlow) {
      dispatch(setSelectedTaxCenterFlow(""));
      if (fromGuard) {
        setIsPolicySelectionOpen(false);
        policyOrAccountDataHandler(selectedRadioValue);
        navigate(-1);
      }
      return;
    }
    policyOrAccountDataHandler(tempData);
  };

  const handleContinueClick = () => {
    if (fromGuard || taxCenterFlow) {
      policyOrAccountDataHandler(selectedRadioValue);
      setIsPolicySelectionOpen(false);
    } else {
      let dynamicState: any = {};
      if (data.lobDetails?.lob === HlLobCode) {
        dynamicState.accountNumber = selectedRadioValue;
        dynamicState.functionalityCode = data.functionalityCode;
      } else {
        dynamicState.policyNumber = selectedRadioValue;
      }
      navigate(data.redirectionUrl, { state: dynamicState });
    }
  };

  return (
    <CustomPopup
      open={data.isOpen}
      handleOpen={handleCloseClick}
      btnLabel={
        fromGuard
          ? taxCenterFlow
            ? t("generalApp.actionButtons.close") || "Close"
            : ""
          : t("generalApp.actionButtons.close") || "Close"
      }
      className="common-custom-popup-style custom-policy-or-account-select "
      btnStartIcon={<CloseIcon sx={{ fontSize: "1.0625rem" }} />}
    >
      <Box
        display="flex"
        justifyContent="center"
        flexDirection="column"
        alignItems="center"
      >
        <Typography
          textTransform="uppercase"
          variant="h5"
          letterSpacing="0.0938rem"
          textAlign="center"
          marginY="1.5rem"
        >
          {data.lobDetails?.lob === HlLobCode
            ? t("generalApp.titles.selectAccountNumber") ||
              "Select Account Number"
            : t("generalApp.titles.selectPolicyNumber") ||
              "Select Policy Number"}
        </Typography>
        <RadioGroup sx={{ gap: "1rem", width: "100%" }} className="radio-group">
          {data.options.map((item: string, index) => (
            <Fragment key={item}>
              <Stack
                sx={{
                  borderRadius: ".75rem",
                  padding: ".75rem 0rem",
                }}
                flexDirection="row"
                display="flex"
                justifyContent="space-between"
                onClick={() => setSelectedRadioValue(item)}
              >
                <Box display="flex" alignItems="center">
                  <Typography
                    marginLeft=".625rem"
                    variant="h4"
                    fontWeight={500}
                  >
                    {item}
                  </Typography>
                </Box>
                <Radio
                  checked={item === selectedRadioValue}
                  sx={{
                    padding: "0 1rem",
                    color: Colors.red,
                    "&.Mui-checked": {
                      color: Colors.red,
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                    },
                  }}
                />
              </Stack>
              {index < data.options.length - 1 && <Divider />}
            </Fragment>
          ))}
        </RadioGroup>
      </Box>
      <Box marginTop="2.25rem" paddingBottom="0.5rem">
        {isBtnLoading ? (
          <CustomThreeDotsLoader />
        ) : (
          <Button
            fullWidth
            variant="contained"
            disabled={!selectedRadioValue}
            className="continue-btn"
            onClick={handleContinueClick}
          >
            {t("generalApp.actionButtons.continue") || "Continue"}
          </Button>
        )}
      </Box>
    </CustomPopup>
  );
};

export default CustomPolicyOrAccountSelect;
